/* eslint-disable react-hooks/exhaustive-deps */
import { Stack, Typography } from '@mui/material';
import { Box } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditAssetFormSchema } from 'utils/validate';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import React, { useEffect, useState, DragEvent, useMemo, useCallback } from 'react';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { ModalAddDocument } from '../../../DocumentsPage/components/ModalCreateDocument';
import { CATEGORY, FormAddDocument } from 'types/documents';
import DeleteIcon from '@mui/icons-material/Delete';
import { TextEllipsis } from '../../../../components/elements/TextEllipsis';
import { TypeDocumentOption } from 'hooks/useGetDocumentOption';
import { ListOptions } from 'app/pages/RoomReportViewPhotoPage/hook';
import { dropdownValueTypes } from 'const/option';
import { capitalizeFirstLetter } from 'utils/convertData';
import { IOptions, OemAttributes } from 'types/room';
import { PhotoManagement } from './PhotoManagement';
import { DatePicker } from 'app/components/elements/DatePicker';
import dayjs from 'dayjs';
import { SelectChangeEvent } from '@mui/material/Select';
import { compact, uniqBy } from 'lodash';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { useManufactureStore } from 'store/manufacture';
import { normalizeTriggerSubTypeId, useTriggerSubTypeStore } from 'store/triggerSubType';
import { entriesInclude } from 'utils/entries';
import { getIssuesConfig } from 'services/option';
import { DEFAULT_CRITICALITY_LEVELS, toCriticalitySelectOptions } from 'utils/issueCriticality';
import { AssetDevice, MaintenanceConfig } from '../../type';
import { Checkbox, FormControlLabel, IconButton } from '@mui/material';
import { NumericInput } from 'app/components/elements/NumericInput';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface AssetType {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: any;
  assetEntries: AssetEntry[];
}

export interface AssetEntry {
  id: number;
  typeId: number;
  fieldId: number;
  createdAt: string;
  updatedAt: string;
  field: Field;
  value?: any;
  index?: number;
  dropdownValueId?: number;
}

export interface Field {
  id: number;
  name: string;
  unit: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  dropdownValueOption?: {
    name: string;
    required: boolean;
    id: number;
  };
}
export interface AssetTypeEntities {
  [key: number]: AssetType;
}

export type TypeEditAssetForm = {
  assetName: string;
  longitude?: string;
  latitude?: string;
  validated: string;
  uploadedFiles?: File[];
  documentOptions?: TypeDocumentOption[];
  modelFiles?: TypeDocumentOption[];
  // criticality: string;
  criticalityKey: string;
  model: string;
  modelName?: string;
  note?: string;
  otherNote?: string;
  manufacturerId?: string;
  manufacturer?: string;
  assetType?: string;
  newPhotos?: File[];
  archivePhotoIds?: number[];
  assetCustomId?: string;
  assetCustomId2?: string;
  lastMaintenance?: string;
  entries?: any[];
  oemAttributes?: {
    label: string;
    value?: string;
    isDropdown?: boolean;
    dropdownId?: number | null;
    dropdownValue?: number | null;
  }[];
  maintenanceEnabled?: boolean; // ← add
  maintenanceConfigs?: MaintenanceConfig[]; // ← add (master, not submitted to API)
  assetDevices?: AssetDevice[]; // ← add
};

// Legacy integer criticality — replaced by criticalityKey from Issues Config
// const criticalities = [
//   { label: '1', value: '1' },
//   { label: '2', value: '2' },
//   { label: '3', value: '3' },
//   { label: '4', value: '4' },
//   { label: '5', value: '5' }
// ];

type EditAssetFormProps = {
  modelSelected?: {
    id: number;
    name: string;
    manufacturer: {
      id: number;
      name: string;
    };
  } | null;
  initValues: Partial<TypeEditAssetForm> & { options: IOptions[] } & Record<string, unknown>;
  onSubmit: (data: TypeEditAssetForm) => void;
  asyncAddDocument: (value: FormAddDocument) => void;
  clientId?: string;
  options: ListOptions;
  existingPhotos?: {
    id: number;
    url: string;
    storage: {
      fileName: string;
      type: string;
    };
  }[];
  /** Hide per-type property blocks (shown separately, e.g. Asset Validation Properties tab). */
  hideDynamicAssetFields?: boolean;
  /** Place Edit / Save / Cancel above the form (validation workflow). */
  editActionsOnTop?: boolean;
  /** Use full width instead of fixed 350px column. */
  fullWidth?: boolean;
  /** Two-column responsive grid for main fields (validation / room asset expand). */
  twoColumnGridLayout?: boolean;
};

export const EditAssetForm = ({
  options,
  modelSelected,
  initValues,
  onSubmit,
  asyncAddDocument,
  clientId,
  existingPhotos = [],
  hideDynamicAssetFields = false,
  editActionsOnTop = false,
  fullWidth = false,
  twoColumnGridLayout = false
}: EditAssetFormProps) => {
  const { systemDropdownValues, singleEntryField, listOption } = useFilteredRoomContext(clientId);
  const storeManufacturers = useManufactureStore((state) => state.manufacturers);
  const { triggerSubTypes: subTypeOptions } = useTriggerSubTypeStore();
  const manufacturerOptions = useMemo(
    () =>
      uniqBy(
        [
          { label: 'Other', value: '0' },
          ...(listOption?.manufactures || []).map((m) => ({
            label: m.name,
            value: m.id.toString()
          })),
          ...storeManufacturers
            .filter((m) => m.value !== 'OTHER')
            .map((m) => ({ label: m.label, value: m.value }))
        ],
        'value'
      ),
    [listOption?.manufactures, storeManufacturers]
  );
  const [showModalAddDocument, setShowModalAddDocument] = useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [criticalityKeyOptions, setCriticalityKeyOptions] = useState(
    toCriticalitySelectOptions(DEFAULT_CRITICALITY_LEVELS)
  );
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

  useEffect(() => {
    getIssuesConfig()
      .then((res) => {
        const levels = res?.config?.criticalityLevels;
        if (levels?.length) {
          setCriticalityKeyOptions(toCriticalitySelectOptions(levels));
        }
      })
      .catch(() => undefined);
  }, []);

  // Photo management states
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<number[]>([]);
  const [assetTypeEntities, setAssetTypeEntities] = useState<AssetTypeEntities>({});
  // OEM Attributes - Dropdown options state
  const oemDropdownOptions = useMemo(() => {
    return (
      systemDropdownValues?.filter((item) =>
        item.options.some((opt) => opt.type === dropdownValueTypes.assetModel)
      ) || []
    );
  }, [systemDropdownValues]);

  // Helper to get dropdown values for a given option ID
  const getOemDropdownValues = useCallback(
    (optionId: number) => {
      const option = oemDropdownOptions.find((opt) => opt.id === optionId);
      return option?.dropdownValues || [];
    },
    [oemDropdownOptions]
  );

  useEffect(() => {
    const ids: number[] = [];
    const entities: AssetTypeEntities = {};
    listOption?.asset_types?.forEach((item) => {
      ids.push(item.id);
      entities[item.id] = item;
    });
    setAssetTypeEntities(entities);
  }, [listOption?.asset_types]);
  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    formState: { errors },
    watch
  } = useForm<TypeEditAssetForm & Record<string, unknown>>({
    resolver: yupResolver(EditAssetFormSchema)
  });
  const selectedModelOemAttributes = useMemo(() => {
    const modelId = watch('model');
    if (!modelId || modelId === 'OTHER' || modelId === '0') return [];

    const latestModel = listOption?.modelsData.find((m) => m.id.toString() === modelId.toString());
    const existingAttrs = Array.isArray(initValues?.oemAttributes)
      ? (initValues.oemAttributes as OemAttributes[])
      : [];

    if (!latestModel) return existingAttrs;

    // API payload might return `oemAttributes` as an object/null instead of an array.
    // Normalize to array to avoid runtime crashes.
    const modelOemAttrs = Array.isArray(latestModel.oemAttributes) ? latestModel.oemAttributes : [];
    if (modelOemAttrs.length === 0) return existingAttrs;

    return modelOemAttrs.map((modelAttr) => {
      const existingAttr = existingAttrs.find((a) => a.label === modelAttr.label);
      return {
        ...modelAttr,
        value: existingAttr ? existingAttr.value : modelAttr.value,
        dropdownValue: existingAttr ? existingAttr.dropdownValue : modelAttr.dropdownValue,
        isDropdown: modelAttr.isDropdown ?? false,
        dropdownId: modelAttr.dropdownId ?? null
      };
    });
  }, [watch('model'), initValues?.oemAttributes, listOption?.modelsData]);

  // Sync merged OEM attributes to form state
  useEffect(() => {
    if (selectedModelOemAttributes.length > 0) {
      const currentOem = watch('oemAttributes');
      if (JSON.stringify(currentOem) !== JSON.stringify(selectedModelOemAttributes)) {
        setValue('oemAttributes', selectedModelOemAttributes, { shouldDirty: false });
      }
    }
  }, [selectedModelOemAttributes, setValue]);
  useEffect(() => {
    const typeId = watch('assetType');
    if (!typeId) return;

    const assetType = assetTypeEntities[Number(typeId)];
    if (!assetType?.assetEntries?.length) return;

    const submittedEntriesMap = new Map<number, any>();
    // @ts-ignore
    initValues?.entries?.forEach((e: any) => {
      submittedEntriesMap.set(e.fieldId, e);
    });

    const entries = assetType.assetEntries.map((item) => {
      const submitted = submittedEntriesMap.get(item.field.id);
      return {
        fieldId: item.field.id,
        value: submitted?.value || '',
        dropdownValueId: submitted?.dropdownValueId || null,
        dropdownOtherValue: submitted?.dropdownOtherValue || null
      };
    });

    setValue('entries', entries, { shouldDirty: false });
  }, [watch('assetType'), assetTypeEntities, initValues]);
  const initialAssetOptions = useMemo(() => {
    if (!initValues) return;
    const assetOptions: Record<string, string> = {};

    initValues.options?.forEach((issueOption) => {
      if (issueOption.fieldType === 'DROPDOWN') {
        systemDropdownValues?.forEach((dropdownValue) => {
          const hasRoomType = dropdownValue.id.toString() === issueOption.type;

          if (hasRoomType) {
            const index = dropdownValue.dropdownValues.some(
              (dropdown) => dropdown.value === issueOption.value
            );

            assetOptions[`option_${issueOption?.type}`] = index ? issueOption?.value : 'OTHER';
            assetOptions[`option_${issueOption?.type}_name`] = issueOption?.value;
          }
        });
      } else {
        assetOptions[`single_${issueOption?.type}`] = issueOption?.value;
      }
    });
    return assetOptions;
  }, [initValues, options]);

  useEffect(() => {
    if (!initValues) return;

    // Reset photo states when form resets
    setNewPhotos([]);
    setDeletedPhotoIds([]);
    // GET responses carry triggerSubTypeId as a plain number — normalize to a
    // string so it matches the Select-bound form fields.
    reset({
      ...initValues,
      ...initialAssetOptions,
      maintenanceEnabled: initValues.maintenanceEnabled ?? false,
      maintenanceConfigs: (initValues.maintenanceConfigs ?? []).map((c) => ({
        ...c,
        triggerSubTypeId: normalizeTriggerSubTypeId(c.triggerSubTypeId)
      })),
      assetDevices: (initValues.assetDevices ?? []).map((d) => ({
        ...d,
        triggerSubTypeId: normalizeTriggerSubTypeId(d.triggerSubTypeId)
      }))
    });
  }, [JSON.stringify(initValues), reset, JSON.stringify(initialAssetOptions)]);
  const modelValue = watch('model');
  const watchedOemAttributes = watch('oemAttributes');
  const oemAttributesToRender = Array.isArray(watchedOemAttributes)
    ? watchedOemAttributes
    : selectedModelOemAttributes;

  const assetIssueOption = useMemo(() => {
    return (
      systemDropdownValues?.filter((item) =>
        item.options.some((opt) => opt.type === dropdownValueTypes.asset)
      ) || []
    );
  }, [systemDropdownValues]);

  // Reset photo states when existingPhotos change (after successful update)
  useEffect(() => {
    setNewPhotos([]);
    setDeletedPhotoIds([]);
  }, [existingPhotos]);

  // Photo management handlers
  const handleAddPhotos = (files: File[]) => {
    setNewPhotos((prev) => [...prev, ...files]);
  };

  const handleDeleteExistingPhoto = (photoId: number) => {
    setDeletedPhotoIds((prev) => [...prev, photoId]);
  };

  const handleRemoveNewPhoto = (index: number) => {
    setNewPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRestorePhoto = (photoId: number) => {
    setDeletedPhotoIds((prev) => prev.filter((id) => id !== photoId));
  };

  const handleFormSubmit = (data: TypeEditAssetForm) => {
    const formDataWithPhotos = {
      ...data,
      newPhotos: newPhotos,
      archivePhotoIds: deletedPhotoIds,
      maintenanceEnabled: data.maintenanceEnabled ?? watch('maintenanceEnabled'),
      assetDevices: data.assetDevices ?? watch('assetDevices')
    };
    setIsEditing(false);
    onSubmit(formDataWithPhotos);
  };
  const assetIssueField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.asset)
    );
  }, [singleEntryField]);

  const { entities, keys } = useMemo(() => {
    const entities: Record<string, AssetEntry[]> = {};

    const typeId = watch('assetType');
    if (!typeId) {
      return { entities, keys: [] as string[] };
    }

    const assetType = assetTypeEntities[Number(typeId)];
    if (!assetType?.assetEntries?.length) {
      return { entities, keys: [] as string[] };
    }

    assetType.assetEntries.forEach((item, index) => {
      const value = watch(`entries.${index}.value`) || '';
      const dropdownValueId = watch(`entries.${index}.dropdownValueId`) || null;
      const dropdownOtherValue = watch(`entries.${index}.dropdownOtherValue`) || null;

      const category = item.field.category || 'GENERAL';

      if (!entities[category]) {
        entities[category] = [];
      }

      entities[category].push({
        ...item,
        value,
        dropdownValueId,
        dropdownOtherValue,
        index
      } as any);
    });
    return {
      entities,
      keys: Object.keys(entities)
    };
  }, [watch('assetType'), JSON.stringify(watch('entries')), assetTypeEntities]);

  // ---- render dynamic asset fields (same behavior as option fields) ----
  const renderDynamicAssetFields = () => {
    if (!keys?.length) return null;

    return keys.map((cat) => (
      <Stack key={cat} gap={2} sx={{ border: '1px solid #eee', p: 2, borderRadius: 2, mt: 2 }}>
        <Typography sx={{ fontWeight: 600 }}>
          {capitalizeFirstLetter(cat.replaceAll('_', ' '))}
        </Typography>

        {entities[cat].map((item) => {
          const baseName = `entries.${item.index}`;

          if (item.field?.dropdownValueOption?.id) {
            const dropdown = systemDropdownValues?.find(
              (d) => d.id === item.field.dropdownValueOption?.id
            );
            return (
              <Stack key={item.index} gap={1}>
                <Controller
                  name={`${baseName}.dropdownValueId`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputSelect
                        disabled={!isEditing}
                        title={`${item.field.name}${item.field.unit ? ` (${item.field.unit})` : ''}`}
                        value={field.value}
                        placeholder={`Select ${item.field.name}`}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        options={
                          dropdown?.dropdownValues.map((dv) => ({
                            label: dv.value,
                            value: dv.id.toString()
                          })) || []
                        }
                      />
                    );
                  }}
                />

                {watch(`${baseName}.dropdownValueId`) === 'OTHER' && (
                  <Controller
                    name={`${baseName}.dropdownOtherValue`}
                    control={control}
                    render={({ field }) => (
                      <InputText
                        title={`${item.field.name} (Other)`}
                        placeholder={`Enter ${item.field.name}`}
                        inputProps={{ ...field, disabled: !isEditing }}
                      />
                    )}
                  />
                )}
              </Stack>
            );
          }

          // TEXT FIELD
          return (
            <Controller
              key={item.index}
              name={`${baseName}.value`}
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  title={`${item.field.name}${item.field.unit ? ` (${item.field.unit})` : ''}`}
                  placeholder={`Enter ${item.field.name}`}
                  inputError={fieldState.error?.message}
                  inputProps={{ ...field, disabled: !isEditing }}
                />
              )}
            />
          );
        })}
      </Stack>
    ));
  };

  const fullRowSx = twoColumnGridLayout ? { gridColumn: '1 / -1' as const } : undefined;

  const editActionButtons = (
    <Stack
      flexDirection={'row'}
      gap={2.5}
      mt={editActionsOnTop ? 0 : 3}
      mb={editActionsOnTop ? 2 : 0}
      width="100%"
      justifyContent={fullWidth || twoColumnGridLayout ? 'flex-start' : 'center'}
      sx={twoColumnGridLayout ? fullRowSx : undefined}>
      <ButtonCustom
        sx={{ width: '150px', height: '46px', padding: '0 20px' }}
        label={isEditing ? 'Save' : 'Edit'}
        onClick={isEditing ? handleSubmit(handleFormSubmit) : () => setIsEditing(true)}
      />
      {isEditing && (
        <ButtonCustom
          sx={{ width: '150px', height: '46px', padding: '0 20px' }}
          label={'Cancel'}
          isSecondary
          onClick={() => {
            reset({ ...initValues, ...initialAssetOptions });
            setIsEditing(false);
          }}
        />
      )}
    </Stack>
  );

  const formGridSx = twoColumnGridLayout
    ? {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
        alignItems: 'start',
        '& > *': { minWidth: 0 }
      }
    : { width: '100%' };

  return (
    <Stack
      width={fullWidth || twoColumnGridLayout ? '100%' : 350}
      maxWidth={fullWidth && !twoColumnGridLayout ? '100%' : undefined}
      alignItems={fullWidth || twoColumnGridLayout ? 'stretch' : 'center'}
      justifyContent={'flex-start'}>
      {editActionsOnTop && editActionButtons}
      <fieldset disabled={!isEditing} style={{ width: '100%', outline: 'unset', border: 'unset' }}>
        <Box sx={formGridSx}>
          <InputText
            inputError={errors.assetName?.message}
            placeholder={'Asset Name'}
            title={'Asset Name'}
            inputProps={{
              ...register('assetName'),
              disabled: !isEditing
            }}
          />
          <Stack style={{ width: '100%', marginBottom: '10px' }}>
            <Controller
              control={control}
              render={({ field: { value } }) => (
                <InputSelect
                  title={'Asset Type'}
                  onChange={(e) => {
                    const selectedTypeId = e.target.value as string;
                    setValue('assetType', selectedTypeId);

                    const typeModels = compact(
                      listOption?.modelsData?.filter((m) => m.type?.id === Number(selectedTypeId))
                    );
                    if (typeModels?.length > 0) {
                      const autoModel = typeModels[0];
                      setValue('model', autoModel.id.toString());
                      if (autoModel?.manufacturer?.id) {
                        setValue('manufacturerId', autoModel.manufacturer.id.toString());
                      } else {
                        setValue('manufacturerId', '');
                      }
                      if (Array.isArray(autoModel?.oemAttributes)) {
                        setValue(
                          'oemAttributes',
                          autoModel.oemAttributes.map((attr) => ({
                            label: attr.label,
                            value: attr.value,
                            isDropdown: attr.isDropdown ?? false,
                            dropdownId: attr.dropdownId ?? null,
                            dropdownValue: attr.dropdownValue ?? null
                          }))
                        );
                      } else {
                        setValue('oemAttributes', []);
                      }
                    } else {
                      setValue('model', '');
                      setValue('manufacturerId', '');
                      setValue('oemAttributes', []);
                    }
                  }}
                  disabled={!isEditing}
                  inputError={errors.assetType?.message}
                  value={value}
                  placeholder={'Select Asset'}
                  sx={{ padding: 0 }}
                  options={uniqBy(
                    listOption?.asset_types?.map((item) => ({
                      value: item?.id.toString() || '',
                      label: item.name || ''
                    })),
                    'value'
                  )}
                />
              )}
              name={'assetType'}
            />
          </Stack>
          <Stack style={{ width: '100%', marginBottom: '10px' }}>
            <Controller
              control={control}
              render={({ field: { value } }) => {
                const selectedAssetTypeId = watch('assetType');
                let availableModels = compact(
                  selectedAssetTypeId
                    ? listOption?.modelsData?.filter(
                        (m) => m.type?.id === Number(selectedAssetTypeId)
                      )
                    : listOption?.modelsData
                );

                if (value && value !== 'OTHER' && value !== '0' && selectedAssetTypeId) {
                  const isValueInAvailable = availableModels.some(
                    (m) => m.id.toString() === value.toString()
                  );
                  if (!isValueInAvailable) {
                    const currentModel = listOption?.modelsData.find(
                      (m) => m.id.toString() === value.toString()
                    );
                    if (currentModel) {
                      availableModels = [...availableModels, currentModel];
                    } else if (modelSelected && modelSelected.id.toString() === value.toString()) {
                      availableModels = [...availableModels, modelSelected as any];
                    }
                  }
                }
                const modelOptions =
                  availableModels.length > 0
                    ? [
                        { label: 'Other', value: 'OTHER' },
                        ...availableModels.map((m) => ({
                          label: m.name,
                          value: m.id.toString()
                        }))
                      ]
                    : [];
                return (
                  <InputSelect
                    disabled={!isEditing}
                    title={'Model'}
                    onChange={(e) => {
                      const v = e.target.value as string;
                      setValue('model', v);
                      if (v === 'OTHER' || v === '0') {
                        setValue('oemAttributes', []);
                        return;
                      }
                      const model = listOption?.modelsData.find((m) => m.id === Number(v));
                      if (model?.manufacturer?.id) {
                        setValue('manufacturerId', model.manufacturer.id.toString());
                      }
                      if (Array.isArray(model?.oemAttributes)) {
                        setValue(
                          'oemAttributes',
                          model.oemAttributes.map((attr) => ({
                            label: attr.label,
                            value: attr.value,
                            isDropdown: attr.isDropdown ?? false,
                            dropdownId: attr.dropdownId ?? null,
                            dropdownValue: attr.dropdownValue ?? null
                          }))
                        );
                      } else {
                        setValue('oemAttributes', []);
                      }
                    }}
                    inputError={errors.model?.message}
                    value={value}
                    placeholder={'Select Model'}
                    sx={{ padding: 0 }}
                    options={modelOptions}
                  />
                );
              }}
              name={'model'}
            />
          </Stack>

          {modelValue === 'OTHER' ? (
            <>
              <Stack sx={{ width: '100%', marginBottom: '10px', ...fullRowSx }}>
                <InputText
                  title={'Model Name'}
                  inputError={errors.modelName?.message}
                  inputProps={{
                    ...register('modelName'),
                    disabled: !isEditing
                  }}
                />
                <Controller
                  control={control}
                  render={({ field: { value } }) => (
                    <InputSelect
                      disabled={!isEditing}
                      title={'Manufacturers'}
                      onChange={(e) => {
                        setValue('manufacturerId', e.target.value as string);
                      }}
                      inputError={errors.model?.message}
                      value={value}
                      placeholder={'Select Manufacturers'}
                      sx={{ padding: 0 }}
                      options={manufacturerOptions}
                    />
                  )}
                  name={'manufacturerId'}
                />
              </Stack>
            </>
          ) : (
            <Stack style={{ width: '100%', marginBottom: '10px' }}>
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <InputSelect
                    disabled={!isEditing}
                    title={'Manufacturers'}
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      onChange(e.target.value);
                    }}
                    inputError={errors.model?.message}
                    value={value}
                    placeholder={'Select Manufacturer'}
                    sx={{ padding: 0 }}
                    options={manufacturerOptions}
                  />
                )}
                name={'manufacturerId'}
              />
            </Stack>
          )}
          {(watch('manufacturerId') === 'OTHER' || watch('manufacturerId') === '0') && (
            <InputText
              title={'Manufacturers Name'}
              inputError={errors.manufacturer?.message}
              inputProps={{
                ...register('manufacturer'),
                placeholder: 'Enter Manufacturers Name',
                disabled: !isEditing
              }}
            />
          )}

          {/* OEM Attributes Section */}
          {selectedModelOemAttributes.length > 0 && (
            <Stack
              gap={1}
              sx={{
                mt: 2,
                mb: 2,
                p: 2,
                border: '1px solid #eee',
                borderRadius: 2,
                ...fullRowSx
              }}>
              <Typography fontWeight={700} color={'#226AAA'}>
                OEM Attributes
              </Typography>
              {oemAttributesToRender.map((attr: OemAttributes, index: number) => {
                const baseName = `oemAttributes.${index}`;

                if (attr.isDropdown && attr.dropdownId) {
                  // Dropdown type OEM attribute
                  const dropdownValues = getOemDropdownValues(attr.dropdownId);
                  return (
                    <Stack key={index} gap={1}>
                      <Controller
                        name={`${baseName}.dropdownValue`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <InputSelect
                            disabled={!isEditing}
                            title={attr.label}
                            value={value ? value : ''}
                            placeholder={`Select ${attr.label}`}
                            onChange={(e: SelectChangeEvent<unknown>) => {
                              const val = e.target.value;
                              onChange(val === '' ? null : Number(val));
                              // Also set displayed value
                              const selectedOption = dropdownValues.find(
                                (dv) => dv.id === Number(val)
                              );
                              if (selectedOption) {
                                setValue(
                                  `${baseName}.value` as any,
                                  selectedOption.value || selectedOption.name
                                );
                              }
                            }}
                            options={[
                              { value: '', label: 'Select...' },
                              ...dropdownValues.map((dv) => ({
                                value: dv.id.toString(),
                                label: dv.value || dv.name
                              }))
                            ]}
                          />
                        )}
                      />
                    </Stack>
                  );
                }

                // Text type OEM attribute
                return (
                  <Controller
                    key={index}
                    name={`${baseName}.value`}
                    control={control}
                    render={({ field }) => (
                      <InputText
                        title={attr.label}
                        placeholder={attr.label}
                        inputProps={{
                          ...field,
                          disabled: !isEditing
                        }}
                      />
                    )}
                  />
                );
              })}
            </Stack>
          )}

          {(initValues?.maintenanceConfigs?.length ?? 0) > 0 && (
            <Box sx={fullRowSx}>
              <Stack gap={2} sx={{ border: '1px solid #eee', p: 2, borderRadius: 2, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Controller
                      control={control}
                      name="maintenanceEnabled"
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          disabled={!isEditing}
                          checked={!!value}
                          onChange={(e) => {
                            onChange(e.target.checked);
                            if (!e.target.checked) {
                              setValue('assetDevices', []);
                            } else {
                              const master = (watch('maintenanceConfigs') ||
                                []) as MaintenanceConfig[];
                              setValue(
                                'assetDevices',
                                master.map((c) => ({
                                  maintenanceTriggerType: c.maintenanceTriggerType,
                                  triggerSubTypeId: c.triggerSubTypeId ?? null,
                                  frequencyType: c.frequencyType,
                                  maintenanceFrequency: c.maintenanceFrequency ?? null,
                                  minMaintenanceFrequency: c.minMaintenanceFrequency ?? null,
                                  maxMaintenanceFrequency: c.maxMaintenanceFrequency ?? null,
                                  notificationWindow: c.notificationWindow ?? null,
                                  counterSource: c.counterSource,
                                  currentReadingValue: null,
                                  iotDeviceId: null,
                                  iotDeviceName: null,
                                  nextMaintenanceDueAt: null,
                                  remainingHours: null,
                                  lastMaintenanceDate: null,
                                  lastMaintenanceHours: null,
                                  maintenanceStatus: null,
                                  lastIotCommunication: null,
                                  connectionStatus: null
                                }))
                              );
                            }
                          }}
                        />
                      )}
                    />
                  }
                  label={<Typography fontWeight={700}>Enable Maintenance Schedule</Typography>}
                />

                {watch('maintenanceEnabled') && (
                  <Stack gap={2}>
                    {((watch('assetDevices') || []) as AssetDevice[]).map((config, index) => {
                      const isIot = config.counterSource === 'IOT';
                      const frequencyType = watch(`assetDevices.${index}.frequencyType`);
                      return (
                        <Stack
                          key={index}
                          gap={1}
                          sx={{ border: '1px solid #D0D9E4', borderRadius: '10px', p: 2 }}>
                          {/* Header */}
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between">
                            <Typography>
                              Trigger Type: {config.maintenanceTriggerType}
                              {config.triggerSubTypeId
                                ? ` (${
                                    subTypeOptions.find((s) => s.value === config.triggerSubTypeId)
                                      ?.label ?? config.triggerSubTypeId
                                  })`
                                : ''}
                            </Typography>
                            {isEditing && (watch('assetDevices') as AssetDevice[]).length > 1 && (
                              <IconButton onClick={() => setPendingRemoveIndex(index)}>
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Stack>

                          {/* frequencyType */}
                          <Controller
                            control={control}
                            name={`assetDevices.${index}.frequencyType`}
                            render={({ field: { onChange, value }, fieldState }) => (
                              <InputSelect
                                disabled={!isEditing}
                                title={'Frequency Type'}
                                value={value || ''}
                                placeholder={'Select Frequency Type'}
                                onChange={(e: SelectChangeEvent<unknown>) =>
                                  onChange(e.target.value as string)
                                }
                                sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                                options={[
                                  { value: 'INTERVAL', label: 'Interval' },
                                  { value: 'RANGE', label: 'Range' }
                                ]}
                                inputError={fieldState.error?.message}
                              />
                            )}
                          />

                          {/* INTERVAL */}
                          {frequencyType === 'INTERVAL' && (
                            <Controller
                              name={`assetDevices.${index}.maintenanceFrequency`}
                              control={control}
                              render={({ field: { onChange, value, ...rest }, fieldState }) => (
                                <NumericInput
                                  title={'Maintenance Frequency'}
                                  placeholder={'e.g. 500'}
                                  inputError={fieldState.error?.message}
                                  value={value}
                                  onChange={onChange}
                                  restProps={{ ...rest, disabled: !isEditing }}
                                />
                              )}
                            />
                          )}

                          {/* RANGE */}
                          {frequencyType === 'RANGE' && (
                            <>
                              <Controller
                                name={`assetDevices.${index}.minMaintenanceFrequency`}
                                control={control}
                                render={({ field: { onChange, value, ...rest }, fieldState }) => (
                                  <NumericInput
                                    title={'Min Maintenance Frequency'}
                                    placeholder={'e.g. 10'}
                                    inputError={fieldState.error?.message}
                                    value={value}
                                    onChange={onChange}
                                    restProps={{ ...rest, disabled: !isEditing }}
                                  />
                                )}
                              />
                              <Controller
                                name={`assetDevices.${index}.maxMaintenanceFrequency`}
                                control={control}
                                render={({ field: { onChange, value, ...rest }, fieldState }) => (
                                  <NumericInput
                                    title={'Max Maintenance Frequency'}
                                    placeholder={'e.g. 30'}
                                    inputError={fieldState.error?.message}
                                    value={value}
                                    onChange={onChange}
                                    restProps={{ ...rest, disabled: !isEditing }}
                                  />
                                )}
                              />
                            </>
                          )}

                          {/* notificationWindow */}
                          <Controller
                            name={`assetDevices.${index}.notificationWindow`}
                            control={control}
                            render={({ field: { onChange, value, ...rest }, fieldState }) => (
                              <NumericInput
                                title={'Notification Window (hours)'}
                                placeholder={'e.g. 24'}
                                inputError={fieldState.error?.message}
                                value={value}
                                onChange={onChange}
                                restProps={{ ...rest, disabled: !isEditing }}
                              />
                            )}
                          />

                          {/* currentReadingValue */}
                          <Controller
                            name={`assetDevices.${index}.currentReadingValue`}
                            control={control}
                            render={({ field: { onChange, value, ...rest }, fieldState }) => (
                              <NumericInput
                                title={'Current Reading Value'}
                                placeholder={'e.g. 100'}
                                inputError={fieldState.error?.message}
                                value={value}
                                onChange={onChange}
                                restProps={{ ...rest, disabled: !isEditing }}
                              />
                            )}
                          />

                          {/* IoT fields */}
                          {isIot && (
                            <>
                              <Controller
                                name={`assetDevices.${index}.iotDeviceId`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <InputText
                                    title={'IoT Device ID'}
                                    placeholder={'Enter IoT Device ID'}
                                    inputError={fieldState.error?.message}
                                    inputProps={{ ...field, disabled: !isEditing }}
                                  />
                                )}
                              />
                              <Controller
                                name={`assetDevices.${index}.iotDeviceName`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <InputText
                                    title={'IoT Device Name'}
                                    placeholder={'Enter IoT Device Name'}
                                    inputError={fieldState.error?.message}
                                    inputProps={{ ...field, disabled: !isEditing }}
                                  />
                                )}
                              />
                            </>
                          )}

                          {/* Read-only fields — always show, null displays as — */}
                          <Stack gap={0.5} sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                            {[
                              { label: 'Next Maintenance Due', value: config.nextMaintenanceDueAt },
                              { label: 'Remaining Hours', value: config.remainingHours },
                              { label: 'Last Maintenance Date', value: config.lastMaintenanceDate },
                              {
                                label: 'Last Maintenance Hours',
                                value: config.lastMaintenanceHours
                              },
                              { label: 'Maintenance Status', value: config.maintenanceStatus },
                              ...(isIot
                                ? [
                                    {
                                      label: 'Last IoT Communication',
                                      value: config.lastIotCommunication
                                    },
                                    { label: 'Connection Status', value: config.connectionStatus }
                                  ]
                                : [])
                            ].map(({ label, value }) => (
                              <Stack
                                key={label}
                                flexDirection="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ py: 0.5 }}>
                                <Typography fontSize={13} color="text.secondary">
                                  {label}
                                </Typography>
                                <Typography fontSize={13} fontWeight={500}>
                                  {value ?? '—'}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Stack>
                      );
                    })}

                    {/* Add missing configs from master */}
                    {isEditing &&
                      (() => {
                        const master = (watch('maintenanceConfigs') || []) as MaintenanceConfig[];
                        const current = (watch('assetDevices') || []) as AssetDevice[];
                        // Keyed by trigger type + sub type so Runtime's multiple
                        // sub-type configs are each tracked independently.
                        const configKey = (c: {
                          maintenanceTriggerType: string;
                          triggerSubTypeId?: string | number | null; // ← accept number too
                        }) =>
                          `${c.maintenanceTriggerType}::${c.triggerSubTypeId != null ? String(c.triggerSubTypeId) : ''}`;
                        const usedKeys = new Set(current.map(configKey));
                        const missing = master.filter((c) => !usedKeys.has(configKey(c)));
                        if (!missing.length) return null;
                        return (
                          <Stack gap={1}>
                            <Typography fontSize={13} color="text.secondary">
                              Missing configs from model:
                            </Typography>
                            {missing.map((c) => (
                              <ButtonCustom
                                key={configKey(c)}
                                label={`+ Add "${c.maintenanceTriggerType}${
                                  c.triggerSubTypeId
                                    ? ` (${
                                        subTypeOptions.find((s) => s.value === c.triggerSubTypeId)
                                          ?.label ?? c.triggerSubTypeId
                                      })`
                                    : ''
                                }"`}
                                style={{ width: 'fit-content' }}
                                onClick={() => {
                                  const next: AssetDevice = {
                                    maintenanceTriggerType: c.maintenanceTriggerType,
                                    triggerSubTypeId: c.triggerSubTypeId ?? null,
                                    frequencyType: c.frequencyType,
                                    maintenanceFrequency: c.maintenanceFrequency ?? null,
                                    minMaintenanceFrequency: c.minMaintenanceFrequency ?? null,
                                    maxMaintenanceFrequency: c.maxMaintenanceFrequency ?? null,
                                    notificationWindow: c.notificationWindow ?? null,
                                    counterSource: c.counterSource,
                                    currentReadingValue: null,
                                    iotDeviceId: null,
                                    iotDeviceName: null,
                                    nextMaintenanceDueAt: null,
                                    remainingHours: null,
                                    lastMaintenanceDate: null,
                                    lastMaintenanceHours: null,
                                    maintenanceStatus: null,
                                    lastIotCommunication: null,
                                    connectionStatus: null
                                  };
                                  setValue('assetDevices', [...current, next]);
                                }}
                              />
                            ))}
                          </Stack>
                        );
                      })()}
                  </Stack>
                )}
              </Stack>
            </Box>
          )}

          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                disabled={!isEditing}
                inputError={errors.validated?.message as string}
                title={'Validated'}
                value={value}
                placeholder={'Validated'}
                onChange={onChange}
                sx={{ padding: 0 }}
                options={[
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' }
                ]}
              />
            )}
            name={'validated'}
          />
          <Stack style={{ width: '100%', marginBottom: '10px' }}>
            <InputText
              title={'Asset ID'}
              inputError={errors.assetCustomId?.message}
              placeholder="Enter Asset ID"
              inputProps={{
                ...register('assetCustomId'),
                disabled: !isEditing
              }}
            />
          </Stack>
          <Stack style={{ width: '100%', marginBottom: '10px' }}>
            <InputText
              title={'Asset ID 2'}
              placeholder="Enter Asset ID 2"
              inputError={errors.assetCustomId2?.message}
              inputProps={{
                ...register('assetCustomId2'),
                disabled: !isEditing
              }}
            />
          </Stack>

          <Stack style={{ width: '100%', marginBottom: '10px' }}>
            {/* Legacy criticality (1–5) select — use criticalityKey from Issues config */}
            <Controller
              control={control}
              render={({ field: { value } }) => (
                <InputSelect
                  disabled={!isEditing}
                  title={'Criticality'}
                  onChange={(e) => {
                    setValue('criticalityKey', e.target.value as string);
                  }}
                  inputError={errors.criticalityKey?.message}
                  value={value}
                  placeholder={'Select Criticality'}
                  sx={{ padding: 0 }}
                  options={criticalityKeyOptions}
                />
              )}
              name={'criticalityKey'}
            />
          </Stack>
          <InputText
            inputError={errors.longitude?.message}
            placeholder={'Enter Longitude'}
            title={'Longitude'}
            inputProps={{
              ...register('longitude'),
              disabled: !isEditing
            }}
          />
          <InputText
            inputError={errors.latitude?.message}
            placeholder={'Enter latitude'}
            title={'Latitude'}
            inputProps={{
              ...register('latitude'),
              disabled: !isEditing
            }}
          />
          <Stack sx={{ width: '100%', marginBottom: '10px', ...fullRowSx }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
              Upload Document
            </Typography>
            <input
              disabled={!isEditing}
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  setValue('uploadedFiles', [
                    ...(watch('uploadedFiles') || []),
                    ...Array.from(files)
                  ]);
                }
              }}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                setIsDragging(false);
                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                  setValue('uploadedFiles', [
                    ...(watch('uploadedFiles') || []),
                    ...Array.from(files)
                  ]);
                }
              }}
              sx={{
                width: '100%',
                height: 120,
                border: '1px dashed #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: '4px',
                bgcolor: isDragging ? '#e3f2fd' : '#f5f5f5',
                transition: 'background-color 0.2s'
              }}>
              <UploadFileIcon sx={{ color: isDragging ? '#1976d2' : '#888' }} />
            </Box>

            {watch('modelFiles')?.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  marginTop: '4px',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  fontSize: 13,
                  backgroundColor: '#fafafa'
                }}>
                <TextEllipsis text={item.storage.fileName} width={'90%'} />
              </Box>
            ))}
            {watch('documentOptions')?.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  marginTop: '4px',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  fontSize: 13,
                  backgroundColor: '#fafafa'
                }}>
                <TextEllipsis text={item.storage.fileName} width={'90%'} />
                <DeleteIcon
                  sx={{ ml: 1, color: 'red', cursor: 'pointer' }}
                  onClick={() => {
                    const updatedFiles = watch('documentOptions')?.filter((_, i) => i !== index);
                    setValue('documentOptions', updatedFiles);
                  }}
                />
              </Box>
            ))}
            {watch('uploadedFiles')?.map((file, index) => {
              if (!file) return;
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    marginTop: '4px',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    fontSize: 13,
                    backgroundColor: '#fafafa'
                  }}>
                  <TextEllipsis text={file?.name} width={'90%'} />
                  <DeleteIcon
                    sx={{ ml: 1, color: 'red', cursor: 'pointer' }}
                    onClick={() => {
                      const updatedFiles = watch('uploadedFiles')?.filter((_, i) => i !== index);
                      setValue('uploadedFiles', updatedFiles);
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
          {assetIssueOption?.map((opt) => {
            const fieldName = `option_${opt.id}`;
            const otherFieldName = `option_${opt.id}_name`;
            const inner = (
              <>
                <Controller
                  name={fieldName}
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputSelect
                      disabled={!isEditing}
                      title={capitalizeFirstLetter(opt.name.replaceAll('_', ' '))}
                      value={field.value}
                      placeholder={`Select ${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))}`}
                      inputError={fieldState.error?.message}
                      onChange={(e: SelectChangeEvent<unknown>) => {
                        const val = e.target.value;
                        field.onChange(val);

                        if (val === 'OTHER') {
                          setValue('actionBy', '');
                        }
                      }}
                      options={opt.dropdownValues.map((dv) => ({
                        label: dv.value,
                        value: dv.value
                      }))}
                    />
                  )}
                />

                {watch(fieldName) === 'OTHER' && (
                  <Controller
                    name={otherFieldName}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <InputText
                        title={`${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))} Name`}
                        placeholder={`${capitalizeFirstLetter(opt.name.replaceAll('_', ' '))} Name`}
                        inputError={fieldState.error ? 'This field is required' : ''}
                        inputProps={{
                          ...field,
                          disabled: !isEditing,
                          onChange: (e: any) => {
                            field.onChange(e.target.value);
                            setValue('actionBy', '');
                          }
                        }}
                      />
                    )}
                  />
                )}
              </>
            );
            return twoColumnGridLayout ? (
              <Box key={opt.id} sx={{ display: 'contents' }}>
                {inner}
              </Box>
            ) : (
              <React.Fragment key={opt.id}>{inner}</React.Fragment>
            );
          })}
          {assetIssueField?.map((field) => {
            const fieldInner =
              field.type === 'DATE' ? (
                <Controller
                  name={`single_${field.id}`}
                  control={control}
                  render={({ field: f }) => (
                    <DatePicker
                      disabled={!isEditing}
                      title={`${field.name}:`}
                      onChange={(val) =>
                        setValue(`single_${field.id}`, val ? dayjs(val).toISOString() : '')
                      }
                      value={f.value ? dayjs(f?.value as string) : undefined}
                    />
                  )}
                />
              ) : (
                <Controller
                  name={`single_${field.id}`}
                  control={control}
                  render={({ field: f, fieldState }) => (
                    <InputText
                      title={`${field.name}:`}
                      placeholder={`Enter ${field.name}`}
                      inputProps={{ ...f, disabled: !isEditing }}
                      inputError={fieldState.error?.message}
                    />
                  )}
                />
              );
            return twoColumnGridLayout ? (
              <Box key={field.id} sx={{ display: 'contents' }}>
                {fieldInner}
              </Box>
            ) : (
              <React.Fragment key={field.id}>{fieldInner}</React.Fragment>
            );
          })}
          <Box sx={fullRowSx}>
            <Controller
              control={control}
              render={({ field: { value } }) => (
                <DatePicker
                  disabled={!isEditing}
                  inputError={errors?.lastMaintenance?.message}
                  style={{ flexDirection: 'column', width: '100%', marginBottom: '10px' }}
                  titleStyle={{ marginBottom: 10 }}
                  title={'Choose Last Maintenance:'}
                  onChange={(val) => {
                    const date = dayjs(val);
                    if (!val || !date.isValid()) {
                      setValue('lastMaintenance', '');
                    } else {
                      setValue('lastMaintenance', date.toISOString());
                    }
                  }}
                  value={value ? dayjs(value) : undefined}
                />
              )}
              name={'lastMaintenance'}
            />
          </Box>

          <Box sx={fullRowSx}>
            <InputText
              placeholder={`Enter Notes`}
              title={`Notes:`}
              inputProps={{
                ...register(`note`),
                disabled: !isEditing
              }}
            />
          </Box>
          <Box sx={fullRowSx}>
            <InputText
              placeholder={`Enter Other Notes`}
              title={`Other Notes:`}
              inputProps={{
                ...register(`otherNote`),
                disabled: !isEditing,
                rows: 3,
                style: {
                  minHeight: '46px',
                  textAlign: 'start',
                  padding: 0
                },
                multiline: true
              }}
            />
          </Box>

          <Box sx={fullRowSx}>
            <PhotoManagement
              disabled={!isEditing}
              existingPhotos={existingPhotos}
              newPhotos={newPhotos}
              deletedPhotoIds={deletedPhotoIds}
              onAddPhotos={handleAddPhotos}
              onDeleteExistingPhoto={handleDeleteExistingPhoto}
              onRemoveNewPhoto={handleRemoveNewPhoto}
              onRestorePhoto={handleRestorePhoto}
            />
          </Box>
          {!hideDynamicAssetFields && <Box sx={fullRowSx}>{renderDynamicAssetFields()}</Box>}
        </Box>
      </fieldset>
      {!editActionsOnTop && editActionButtons}
      <ModalAddDocument
        category={Object.entries(CATEGORY).map(([key, value]) => ({
          label: key,
          value: value
        }))}
        onCloseModal={() => setShowModalAddDocument(false)}
        isOpen={showModalAddDocument}
        onSubmit={(value) => {
          setShowModalAddDocument(false);
          asyncAddDocument({ ...value, clientIds: clientId ? [clientId] : [] });
        }}
        clientId={clientId}
      />

      <DialogConfirm
        isOpen={pendingRemoveIndex !== null}
        onClose={() => setPendingRemoveIndex(null)}
        textTitle={'Remove Maintenance Config'}
        textButton={'Remove'}
        textCancel={'Cancel'}
        isDelete
        isIconClose
        textContent={'Are you sure you want to remove this maintenance config?'}
        onSubmit={() => {
          if (pendingRemoveIndex !== null) {
            const current = (watch('assetDevices') || []) as AssetDevice[];
            setValue(
              'assetDevices',
              current.filter((_, i) => i !== pendingRemoveIndex)
            );
            setPendingRemoveIndex(null);
          }
        }}
      />
    </Stack>
  );
};
